import React from 'react';
import { createPortal } from 'react-dom';

import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { keyboardManager, useModalEscape } from '@/shared/services/KeyboardEventManager';
import type { VisibilityTier } from '@/shared/types/projectData';
import { cn } from '@/ui/theme/ThemeContext';

import type { ApprovalRequestScope } from '../services/approvalRequestService';

interface VisibilityApprovalRequestDialogProps {
  isOpen: boolean;
  theme: Record<string, string>;
  targetLabel: string;
  requestedVisibilityTier: VisibilityTier;
  defaultScope: ApprovalRequestScope;
  showSubtreeOption?: boolean;
  alternateScope?: ApprovalRequestScope;
  alternateScopeLabel?: string;
  isSubmitting?: boolean;
  /** A failed submission's reason, shown in the dialog so the user can retry or cancel. */
  errorMessage?: string | null;
  returnFocusTo?: HTMLElement | null;
  onClose: () => void;
  onSubmit: (payload: { scope: ApprovalRequestScope; rationale: string }) => Promise<void> | void;
}

const VISIBILITY_LABELS: Record<VisibilityTier, string> = {
  internal: 'Internal',
  partner: 'Partner',
  client: 'Client',
};

export const VisibilityApprovalRequestDialog: React.FC<VisibilityApprovalRequestDialogProps> = ({
  isOpen,
  theme,
  targetLabel,
  requestedVisibilityTier,
  defaultScope,
  showSubtreeOption = false,
  alternateScope,
  alternateScopeLabel,
  isSubmitting = false,
  errorMessage = null,
  returnFocusTo,
  onClose,
  onSubmit,
}) => {
  const { t } = useSettings();
  const [rationale, setRationale] = React.useState('');
  const [scope, setScope] = React.useState<ApprovalRequestScope>(defaultScope);

  const overlayRef = React.useRef<HTMLDivElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const openerRef = React.useRef<HTMLElement | null>(null);
  const openedRef = React.useRef(false);
  if (isOpen && !openedRef.current && typeof document !== 'undefined') {
    openerRef.current = returnFocusTo ?? (document.activeElement instanceof HTMLElement ? document.activeElement : null);
  }
  openedRef.current = isOpen;
  React.useEffect(() => {
    const overlay = overlayRef.current, view = window.visualViewport;
    if (!isOpen || !overlay || !view) return undefined;
    const update = () => {
      const editing = overlay.contains(document.activeElement) && document.activeElement?.matches('textarea,input');
      const keyboard = (editing || overlay.dataset.keyboardViewport === 'true') && view.scale === 1 && window.innerHeight - view.height > 40;
      overlay.dataset.keyboardViewport = String(Boolean(keyboard));
      overlay.style.setProperty('--pm-approval-visible-top', `${view.offsetTop}px`);
      overlay.style.setProperty('--pm-approval-visible-height', `${view.height}px`);
    };
    view.addEventListener('resize', update);view.addEventListener('scroll', update);
    overlay.addEventListener('focusin', update);overlay.addEventListener('focusout', update);update();
    return () => {view.removeEventListener('resize', update);view.removeEventListener('scroll', update);overlay.removeEventListener('focusin', update);overlay.removeEventListener('focusout', update);};
  }, [isOpen]);
  React.useLayoutEffect(() => {
    const overlay = overlayRef.current;
    if (!isOpen || !overlay) return undefined;
    const background = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== overlay && !element.contains(overlay))
      .map(element => ({ element, inert: element.inert }));
    background.forEach(({ element }) => { element.inert = true; });
    return () => {
      background.forEach(({ element, inert }) => { element.inert = inert; });
    };
  }, [isOpen]);
  React.useEffect(() => {
    const overlay = overlayRef.current;
    if (!isOpen || !overlay) return undefined;
    const target = isSubmitting ? overlay : cancelRef.current;
    if (!overlay.contains(document.activeElement) || document.activeElement?.matches(':disabled')) {
      target?.focus({ preventScroll: true });
    }
  }, [isOpen, isSubmitting]);
  React.useEffect(() => {
    const overlay = overlayRef.current;
    if (!isOpen || !overlay) return undefined;
    const controls = () => Array.from(overlay.querySelectorAll<HTMLElement>('*')).filter(element => element.matches('button:not(:disabled),input:not(:disabled),textarea:not(:disabled),select:not(:disabled),[tabindex="0"]'));
    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented || !keyboardManager.isModalOnTop('visibility-approval-request-dialog')) return;
      const items = controls(), first = items[0], last = items[items.length - 1], active = document.activeElement;
      if (!first) {
        event.preventDefault();
        overlay.focus({ preventScroll: true });
        return;
      }
      if (!overlay.contains(active) || (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
        event.preventDefault();(event.shiftKey ? last : first)?.focus();
      }
    };
    document.addEventListener('keydown', handleTab);
    return () => {
      document.removeEventListener('keydown', handleTab);
      if (openerRef.current?.isConnected && !openerRef.current.matches(':disabled')) openerRef.current.focus({ preventScroll: true });
    };
  }, [isOpen]);

  const dismiss = () => { if (!isSubmitting) onClose(); };
  useModalEscape('visibility-approval-request-dialog', dismiss, {
    enabled: isOpen,
    priority: 310,
  });

  React.useEffect(() => {
    if (!isOpen) {
      setRationale('');
      setScope(defaultScope);
    }
  }, [defaultScope, isOpen]);

  if (!isOpen) {
    return null;
  }

  const resolvedAlternateScope = alternateScope
    || (showSubtreeOption && defaultScope === 'node' ? 'subtree' : undefined);
  const resolvedAlternateScopeLabel = alternateScopeLabel
    || (resolvedAlternateScope === 'subtree' ? t('Apply to subtree') : undefined);

  return createPortal(
    <div
      ref={overlayRef}
      role="dialog"
      tabIndex={-1}
      aria-modal="true"
      aria-label={t('Request admin approval')}
      data-testid="visibility-approval-request-dialog"
      className="pm-visibility-request fixed inset-0 z-[1400] flex items-center justify-center bg-black/45 px-4"
      onClick={dismiss}
    >
      <div
        className={cn('w-full max-w-md rounded-2xl border p-4 shadow-2xl', theme.bgPrimary, theme.borderPrimary)}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="space-y-1">
          <div className={cn('text-sm font-semibold', theme.textPrimary)}>{t('Request admin approval')}</div>
          <div className={cn('text-xs leading-5', theme.textSecondary)}>
            {t('Request {{visibility}} visibility for {{target}}.', {
              visibility: t(VISIBILITY_LABELS[requestedVisibilityTier]),
              target: targetLabel,
            })}
          </div>
        </div>

        {resolvedAlternateScope && resolvedAlternateScopeLabel ? (
          <label className={cn('mt-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs', theme.bgSecondary, theme.borderPrimary, theme.textSecondary)}>
            <input
              data-testid="visibility-approval-request-subtree-toggle"
              type="checkbox"
              checked={scope === resolvedAlternateScope}
              onChange={(event) => setScope(event.target.checked ? resolvedAlternateScope : defaultScope)}
              disabled={isSubmitting}
            />
            <span>{resolvedAlternateScopeLabel}</span>
          </label>
        ) : null}

        <label className="mt-3 block space-y-1">
          <span className={cn('text-xs font-medium', theme.textSecondary)}>{t('Rationale')}</span>
          <textarea
            data-testid="visibility-approval-request-rationale"
            value={rationale}
            onChange={(event) => setRationale(event.target.value)}
            disabled={isSubmitting}
            rows={3}
            placeholder={t('Why should this be visible outside the internal boundary?')}
            className={cn('w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none', theme.bgSecondary, theme.borderPrimary, theme.textPrimary)}
          />
        </label>

        {errorMessage ? (
          <div
            role="alert"
            data-testid="visibility-approval-request-error"
            className={cn('mt-3 rounded-lg border px-3 py-2 text-xs text-red-600 dark:text-red-400', theme.borderPrimary)}
          >
            {errorMessage}
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            ref={cancelRef}
            data-testid="visibility-approval-request-cancel"
            onClick={onClose}
            disabled={isSubmitting}
            className={cn('rounded-lg px-3 py-2 text-sm transition-colors', theme.hover, theme.textSecondary)}
          >
            {t('Cancel')}
          </button>
          <button
            type="button"
            data-testid="visibility-approval-request-submit"
            disabled={isSubmitting || !rationale.trim()}
            onClick={() => void onSubmit({ scope, rationale: rationale.trim() })}
            className={cn('rounded-lg px-3 py-2 text-sm font-medium transition-colors', theme.primaryAction)}
          >
            {isSubmitting ? t('Sending...') : t('Send request')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
